import { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Theme,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { makeStyles } from 'tss-react/mui';

import { ChangelogEntry, getChangelog } from '../api/versionsApi/versionsApi';
import Loader from './Loader';

const hideViewCommit = true;

const useStyles = makeStyles()((theme: Theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
      width: '680px',
      maxWidth: '90vw',
      height: '80vh',
      maxHeight: '80vh',
      borderRadius: '12px',
      display: 'flex',
      flexDirection: 'column',
    },
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '18px',
  },
  closeButton: {
    color: theme.palette.common.white,
    padding: '4px',
  },
  content: {
    padding: '8px 24px 24px',
    overflowY: 'auto',
    flexGrow: 1,
    '&::-webkit-scrollbar': {
      width: '5px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  versionBlock: {
    marginBottom: '12px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(118, 120, 133, 0.35)',
    borderRadius: '10px',
    padding: '16px 18px',
  },
  versionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    paddingBottom: '10px',
    borderBottom: '1px solid rgba(118, 120, 133, 0.35)',
  },
  versionNumber: {
    fontFamily: 'Inter',
    fontWeight: 700,
    fontSize: '15px',
    color: theme.palette.common.white,
  },
  versionDate: {
    fontFamily: 'Inter',
    fontSize: '12px',
    color: theme.palette.text.secondary,
  },
  typeBadge: {
    fontFamily: 'Inter',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderRadius: '4px',
    padding: '2px 6px',
    color: theme.palette.common.white,
  },
  sectionLabel: {
    fontFamily: 'Inter',
    fontSize: '11px',
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginTop: '10px',
    marginBottom: '4px',
  },
  sectionBlock: {
    backgroundColor: 'rgba(0,0,0,0.15)',
    borderRadius: '6px',
    padding: '8px 12px',
    marginBottom: '4px',
  },
  changeList: {
    padding: '0 0 0 8px',
    margin: 0,
  },
  changeItem: {
    padding: '2px 0',
    '& .MuiListItemText-primary': {
      fontFamily: 'Inter',
      fontSize: '13px',
      color: theme.palette.common.white,
    },
  },
  commitLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: theme.palette.text.secondary,
    marginTop: '4px',
    fontFamily: 'Inter',
  },
  divider: {
    backgroundColor: 'rgba(118, 120, 133, 0.4)',
    margin: '8px 0',
  },
  emptyText: {
    fontFamily: 'Inter',
    fontSize: '14px',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    padding: '24px 0',
  },
  noChanges: {
    fontFamily: 'Inter',
    fontSize: '12px',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    paddingLeft: '8px',
  },
  fullHistoryFooter: {
    borderTop: '1px solid rgba(118, 120, 133, 0.35)',
    paddingTop: '12px',
    marginTop: '8px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  fullHistoryLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontFamily: 'Inter',
    fontSize: '13px',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
  },
}));

interface ChangelogPopupProps {
  open: boolean;
  onClose: () => void;
}

const ChangelogPopup = ({ open, onClose }: ChangelogPopupProps) => {
  const { classes } = useStyles();
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(false);
    getChangelog()
      .then((data) => setEntries(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog}>
      <DialogTitle className={classes.dialogTitle} component="div">
        <span>What&apos;s New</span>
        <IconButton onClick={onClose} className={classes.closeButton} disableRipple>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Loader colorType="warning" size={48} />
          </Box>
        )}

        {!loading && error && (
          <Typography className={classes.emptyText}>Failed to load changelog.</Typography>
        )}

        {!loading && !error && entries.length === 0 && (
          <Typography className={classes.emptyText}>No changelog entries found.</Typography>
        )}

        {!loading &&
          !error &&
          entries.map((entry) => (
            <Box key={entry.version} className={classes.versionBlock}>
              {/* Version header */}
              <Box className={classes.versionHeader}>
                <Typography className={classes.versionNumber}>{entry.version}</Typography>
                <Typography className={classes.typeBadge}>{entry.type}</Typography>
                <Typography className={classes.versionDate}>{entry.date}</Typography>
              </Box>

              {/* Changes */}
              <Typography className={classes.sectionLabel}>Changes</Typography>
              <Box className={classes.sectionBlock}>
                {entry.changes.length > 0 ? (
                  <List className={classes.changeList} disablePadding>
                    {entry.changes.map((change, i) => (
                      <ListItem key={i} className={classes.changeItem} disableGutters disablePadding>
                        <ListItemText primary={`• ${change}`} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography className={classes.noChanges}>No changes</Typography>
                )}
                {!hideViewCommit && entry.commit && (
                  <Link
                    href={entry.commit}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.commitLink}
                    underline="hover"
                  >
                    View commit <OpenInNewIcon sx={{ fontSize: '11px' }} />
                  </Link>
                )}
              </Box>
            </Box>
          ))}

        {!loading && !error && entries.length > 0 && (
          <Box className={classes.fullHistoryFooter}>
            <Link
              href="/changelog/full"
              target="_blank"
              rel="noopener noreferrer"
              className={classes.fullHistoryLink}
              underline="hover"
            >
              View full history <OpenInNewIcon sx={{ fontSize: '13px' }} />
            </Link>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChangelogPopup;
